'use client'

import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft, PenNib, BookOpen } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'

// Story data - update this with your actual stories from the Google Doc
const stories: Array<{ 
  id: string
  title: string
  description: string
  date?: string
  excerpt?: string
}> = [
  {
    id: 'stuffy-kitchen',
    title: 'Stuffy Kitchen',
    description: 'A story about Twilight and Hank working in the kitchen, told through the perspective of a child playing with plush toys.',
    excerpt: 'It\'s already late and the scrubber is old as fuck and I\'m sick of the tin clinks.'
  },
  {
    id: 'lady-of-the-flies',
    title: 'Lady of the Flies',
    description: 'A tense narrative about an uncomfortable potluck gathering in the forest, where flies and unsettling conversations create an atmosphere of unease.',
    excerpt: 'I couldn\'t stand the feeling of their little grimy limbs clinging to my skin.'
  },
  {
    id: 'pants-again',
    title: 'Pants again',
    description: 'A desperate man searches for clothing after being left stranded, navigating rejection and judgment in his quest for basic dignity.',
    excerpt: 'A cold gust slapped on Jarrod\'s goose-pimpled skin.'
  },
  {
    id: 'a-nice-family',
    title: 'A nice family',
    description: 'A poignant story about grief, healing, and finding connection in the aftermath of loss, told through a train ride and a child\'s perspective.',
    excerpt: 'Without much hesitation the boy climbed onto the seat of the train car, the wheels of his toy car spinning up the vinyl cushion.'
  },
  {
    id: 'hey-there',
    title: 'Hey There',
    description: 'A humorous and unexpected letter from an unusual perspective, exploring themes of work pressure, frustration, and finding relief.',
    excerpt: 'It\'s been a rough week.  When I feel like this, I never know where to start.'
  },
  {
    id: 'stupid-boys',
    title: 'Stupid boys',
    description: 'A poem about the consequences of reckless play and dangerous choices.',
    excerpt: 'They waltzed out to the pasture'
  },
  {
    id: 'my-cult-is-an-awesome-cult',
    title: 'My Cult is an Awesome Cult',
    description: 'A poem exploring themes of belonging, manipulation, and the complex psychology of cult membership.',
    excerpt: 'My cult is an awesome cult'
  }
]

export default function Writing() {
  return (
    <Components.PageContainer className="min-h-screen min-h-[100dvh]">
      <Components.SkipLink />
      <Components.GlassNavigation />
      
      <main id="main-content" tabIndex={-1} className="pt-16 md:pt-32 pb-16 px-4 md:px-6 bg-[var(--brutal-paper)] min-h-screen min-h-[100dvh]" role="main" aria-label="Writing" style={{ backgroundColor: '#faf5ed', minHeight: '100dvh' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="mb-8 md:mb-12">
            <Button
              asChild
              variant="ghost"
              className="mb-6 text-[var(--brutal-ink)] hover:bg-stone-200/50 border-[var(--brutal-ink)]"
            >
              <Link to="/CreativeProjects">
                <ArrowLeft weight="duotone" className="w-4 h-4 mr-2" />
                Back to Creative Projects
              </Link>
            </Button>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 aspect-square bg-gradient-to-br from-slate-600/20 to-slate-700/20 rounded-full flex items-center justify-center flex-shrink-0">
                <BookOpen weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--brutal-ink)] leading-tight break-words">
                  Writing
                </h1>
                <p className="text-base md:text-lg text-[var(--brutal-ink)] mt-2 break-words">
                  Short stories, poems, and prose.
                </p>
              </div>
            </div>
          </header>

          {/* Stories List */}
          {stories.length > 0 ? (
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" aria-label="Writing collection">
              {stories.map((story) => (
                <Card
                  key={story.id}
                  className="group relative overflow-hidden brutal-panel hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#141414] transition-all duration-150 cursor-pointer h-full flex flex-col"
                >
                  <Link to={`/Writing/${story.id}`} className="flex flex-col h-full">
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2 group-hover:text-slate-900 transition-colors duration-200 break-words">
                        {story.title}
                      </h3>
                      {story.date && (
                        <p className="text-[var(--brutal-muted)] text-sm mb-3">{story.date}</p>
                      )}
                      <p className="text-[var(--brutal-ink)] leading-relaxed mb-4 flex-1 break-words">
                        {story.description}
                      </p>
                      {story.excerpt && (
                        <p className="text-[var(--brutal-ink)] text-sm italic mb-4 break-words">
                          "{story.excerpt}"
                        </p>
                      )}
                      <div className="mt-auto">
                        <Button
                          variant="ghost"
                          className="text-[var(--brutal-ink)] group-hover:text-slate-900 border-[var(--brutal-ink)] hover:bg-stone-200/50 w-full"
                        >
                          Read Story
                        </Button>
                      </div>
                    </div>
                  </Link>
                </Card>
              ))}
            </section>
          ) : (
            <Card className="brutal-panel p-12 text-center">
              <p className="text-[var(--brutal-ink)] text-lg">Writing pieces coming soon...</p>
            </Card>
          )}
        </div>
      </main>
    </Components.PageContainer>
  )
}
